/**
 * Shared constants for canvas v2 node sizing.
 *
 * All image/video preview nodes use these maps to ensure consistent
 * card widths based on aspect ratio.
 */

/** CSS aspect-ratio values keyed by display ratio string. */
export const ASPECT_RATIO_MAP: Record<string, string> = {
  '16:9': '16/9',
  '9:16': '9/16',
  '3:4': '3/4',
  '4:3': '4/3',
  '1:1': '1/1',
}

/** Node card widths (px) keyed by aspect ratio.
 *  Must stay in sync with schemas/nodes/imageGenerator.json layout. */
export const ASPECT_WIDTH_MAP: Record<string, number> = {
  '16:9': 315,
  '9:16': 177,
  '3:4': 236,
  '4:3': 315,
  '1:1': 240,
}

/** Numeric ratio values for snapping uploaded images to the closest bucket. */
const ASPECT_RATIOS: Array<{ label: string; ratio: number }> = [
  { label: '16:9', ratio: 16 / 9 },
  { label: '9:16', ratio: 9 / 16 },
  { label: '3:4', ratio: 3 / 4 },
  { label: '4:3', ratio: 4 / 3 },
  { label: '1:1', ratio: 1 },
]

/**
 * Given an image's natural dimensions, return the ASPECT_WIDTH_MAP width
 * for the closest matching aspect ratio bucket.
 */
export function snapToClosestAspectWidth(naturalW: number, naturalH: number): number {
  if (naturalH === 0) return ASPECT_WIDTH_MAP['16:9']
  const imageRatio = naturalW / naturalH
  let closest = ASPECT_RATIOS[0]
  let minDelta = Math.abs(imageRatio - closest.ratio)
  for (let i = 1; i < ASPECT_RATIOS.length; i++) {
    const delta = Math.abs(imageRatio - ASPECT_RATIOS[i].ratio)
    if (delta < minDelta) {
      minDelta = delta
      closest = ASPECT_RATIOS[i]
    }
  }
  return ASPECT_WIDTH_MAP[closest.label]
}
